import React, { useState, useContext, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
  MdNotifications,
  MdSettings,
  MdArrowDropDown,
  MdLogout,
  MdMenu,
} from "react-icons/md";
import NotificationServices from "../services/NotificationServices";
import "../styles/TopBar.css";
import moment from "moment";

const TopBar = ({
  title = "Dashboard",
  showMenuButton = false,
  onMenuClick,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifTab, setNotifTab] = useState("ALL"); // ALL | UNREAD
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const { user, logout } = useContext(AuthContext);
  const history = useHistory();
  const notificationApi = new NotificationServices();

  const handleLogout = () => {
    logout();
    history.push("/login");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality
    console.log("Searching for:", searchQuery);
  };

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await notificationApi.getUnreadCount();
      if (res?.isSuccess) {
        setUnreadCount(res.count || 0);
      }
    } catch (_err) {
      // silent
    }
  }, [notificationApi]);

  const loadNotifications = useCallback(
    async (tab = notifTab) => {
      if (!user) return;
      setNotifLoading(true);
      try {
        const status = tab === "UNREAD" ? "UNREAD" : "ALL";
        const res = await notificationApi.getNotificationList(
          1,
          20,
          "",
          status,
        );
        if (res?.isSuccess) {
          setNotifications(res.data || []);
        }
      } catch (_err) {
        // silent or toast if desired
      } finally {
        setNotifLoading(false);
      }
    },
    [notifTab, notificationApi, user],
  );

  // Auto-refresh badge
  useEffect(() => {
    if (!user) return;
    loadUnreadCount();
    const id = setInterval(() => {
      loadUnreadCount();
    }, 15000);
    return () => clearInterval(id);
  }, [user, loadUnreadCount]);

  const handleToggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) {
      loadNotifications("ALL");
      setNotifTab("ALL");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await notificationApi.markAllRead();
      if (res?.isSuccess) {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
        setUnreadCount(0);
      }
    } catch (_err) {
      // ignore
    }
  };

  const handleClickNotification = async (n) => {
    if (!n || n.is_read) return;
    try {
      await notificationApi.markRead(n.id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, is_read: 1 } : item)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_err) {
      // ignore
    }
  };

  return (
    <header className="topbar">
      <div className="topbar-container">
        {/* Left Section - Title */}
        <div className="topbar-left">
          {showMenuButton && (
            <button
              type="button"
              className="topbar-menu-btn"
              aria-label="Open menu"
              onClick={() => onMenuClick && onMenuClick()}
            >
              <MdMenu />
            </button>
          )}
          <h1 className="topbar-title">{title}</h1>
        </div>

        {/* Center Section - Search */}
        {/* <div className="topbar-center">
          <form className="search-form" onSubmit={handleSearch}>
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search for Menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </form>
        </div> */}

        {/* Right Section - Icons & User Menu */}
        <div className="topbar-right">
          {/* Notifications */}
          <button
            className="topbar-icon-btn"
            title="Notifications"
            onClick={handleToggleNotifications}
          >
            <MdNotifications />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div className="user-menu-wrapper">
            <button
              className="user-menu-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <div className="user-avatar">{user?.name?.charAt(0) || "U"}</div>
              <MdArrowDropDown
                className={`dropdown-icon ${showUserMenu ? "open" : ""}`}
              />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                  <div className="dropdown-user-info">
                    <p className="dropdown-name">{user?.name || "User"}</p>
                    <p className="dropdown-email">{user?.role || "user"}</p>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-menu-ll">
                  <Link to="/profile" className="dropdown-item">
                    👤 My Profile
                  </Link>
                  <Link to="/settings" className="dropdown-item">
                    ⚙️ Settings
                  </Link>
                </div>

                <div className="dropdown-divider" />

                <button className="dropdown-logout" onClick={handleLogout}>
                  <MdLogout />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {showUserMenu && (
        <div
          className="dropdown-overlay"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Notification panel */}
      {showNotifications && (
        <>
          <div className="notif-panel">
            <div className="notif-header">
              <div>
                <h3>Notifications</h3>
              </div>
              <button
                className="notif-close-btn"
                onClick={() => setShowNotifications(false)}
              >
                ×
              </button>
            </div>

            <div className="notif-tabs">
              <button
                className={`notif-tab ${notifTab === "ALL" ? "active" : ""}`}
                onClick={() => {
                  setNotifTab("ALL");
                  loadNotifications("ALL");
                }}
              >
                All
              </button>
              <button
                className={`notif-tab ${notifTab === "UNREAD" ? "active" : ""}`}
                onClick={() => {
                  setNotifTab("UNREAD");
                  loadNotifications("UNREAD");
                }}
              >
                Unread
              </button>
              <button
                className="notif-mark-all"
                onClick={handleMarkAllRead}
                disabled={!unreadCount}
              >
                Mark all as read
              </button>
            </div>

            <div className="notif-list">
              {notifLoading ? (
                <div className="notif-empty">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notif-empty">No notifications</div>
              ) : (
                notifications.map((n) => {
                  const createdAt = n.created_at
                    ? new Date(n.created_at)
                    : null;
                  const initials = (n.title || "Nt")
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s.charAt(0).toUpperCase())
                    .join("");

                  const isReceiver = user && n.receiver_id === user.user_id;
                  const body = (isReceiver && n.receiver_body) || n.body || "";

                  return (
                    <div
                      key={n.id}
                      className={`notif-item ${n.is_read ? "" : "unread"}`}
                      onClick={() => handleClickNotification(n)}
                    >
                      <div className="notif-avatar">{initials}</div>
                      <div className="notif-content">
                        <div className="notif-title">
                          {n.title || "Notification"}
                        </div>
                        <div className="notif-body">{body}</div>
                        {createdAt && (
                          <div className="notif-time">
                            {moment.utc(createdAt).format('DD-MMM-YYYY hh:mm A')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div
            className="notif-overlay"
            onClick={() => setShowNotifications(false)}
          />
        </>
      )}
    </header>
  );
};

export default TopBar;
